// @ts-strict-ignore
import React from 'react'
import { FetchNextPageOptions, InfiniteQueryObserverResult } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Contact, RelationshipTypes } from '@node-space/types'
import { ActionListItem, Avatar, Button, Highlight } from '@node-space/ui-components'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { getContactHighlightLabel } from 'pages/Contacts/utils/utils'
import { ContactOrPaymentInstrumentList } from './ContactOrPaymentInstrumentList'

export interface ContactsSearchListProps {
  contacts: Contact[]
  isFetchingNextPage: boolean
  hasLoadedMore: boolean
  hasNextPage: boolean
  setHasLoadedMore: React.Dispatch<React.SetStateAction<boolean>>
  handleContactSelect: (contact: Contact) => void
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<Contact[], Error>>
  setContactIds?: React.Dispatch<React.SetStateAction<string[]>>
  contactFilter?: string[]
}

export const ContactsSearchList = ({
  contacts,
  isFetchingNextPage,
  hasLoadedMore,
  hasNextPage,
  handleContactSelect,
  setHasLoadedMore,
  fetchNextPage,
  setContactIds,
  contactFilter,
}: ContactsSearchListProps) => {
  const { t } = useTranslation()
  const { enableCryptoFiatSend } = useFeatureFlags()

  return (
    <ContactOrPaymentInstrumentList
      list={contacts}
      isFetchingNextPage={isFetchingNextPage}
      hasLoadedMore={hasLoadedMore}
      hasNextPage={hasNextPage}
      setHasLoadedMore={setHasLoadedMore}
      fetchNextPage={fetchNextPage}
      getItemKey={item => item.id}
      listItemRenderer={(item, index) => {
        const isFirstParty = item.relationshipType === RelationshipTypes.SELF_OWNED

        return (
          <ActionListItem
            key={item.id}
            id={item.id}
            title={item.name}
            testId={`contacts-step-list-item-${index}`}
            HeaderHighlightLeft={
              <Highlight type="neutral" variant="emphasis">
                {getContactHighlightLabel(item, t)}
              </Highlight>
            }
            Avatar={<Avatar size="large" type="entity" name={item.name ?? ''} />}
            onItemClick={() => handleContactSelect(item)}
            ActionHighlight={
              enableCryptoFiatSend && isFirstParty ? (
                <Highlight variant="emphasis" type="feature">
                  {t('contacts.myCompany')}
                </Highlight>
              ) : undefined
            }
          />
        )
      }}
      footerRenderer={
        contactFilter?.length
          ? () => (
              <Button onClick={() => setContactIds([])} variant="ghost">
                {t('contacts.showAll')}
              </Button>
            )
          : undefined
      }
    />
  )
}
